import apiClient from './client';

// ─── Types ────────────────────────────────────────────────────────────────────

export type LeaveRequestStatus = 'pending' | 'approved' | 'rejected' | 'recalled' | 'recalling';

export interface LeaveType {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLeaveTypePayload {
  name: string;
  description?: string;
}

/** Matches the raw LeaveRequestEntity returned by the backend (snake_case). */
export interface LeaveRequest {
  id: string;
  account_id: string;
  monday_user_id: string;
  approver_user_id: string;
  leave_type_id: string;
  date_from: string;
  date_to: string;
  capacity_type: 'full_day' | 'half_day';
  status: LeaveRequestStatus;
  total_days: number;
  note: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateLeaveRequestPayload {
  leaveTypeId: string;
  approverUserId?: string;
  dateFrom: string;
  dateTo: string;
  capacityType: 'full_day' | 'half_day';
  totalDays: number;
  note?: string;
  /** Create leave on behalf of another user (admin or designated approver) */
  forUserId?: string;
}

export interface GetLeaveRequestsParams {
  userId?: number;
  approverId?: number;
  status?: LeaveRequestStatus;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
}

export interface PaginatedLeaveResponse {
  data: LeaveRequest[];
  total: number;
  page: number;
  pageSize: number;
}

export interface LeaveCalendarEntry {
  userId: number;
  userName: string;
  date: string;
  leaveTypeId: string;
  leaveTypeName: string;
  leaveTypeColor: string;
  capacityType: 'full_day' | 'half_day';
}

export interface GetLeaveCalendarParams {
  dateFrom: string;
  dateTo: string;
}

// ─── Leave Types API ──────────────────────────────────────────────────────────

export async function getLeaveTypes(): Promise<LeaveType[]> {
  const { data } = await apiClient.get<LeaveType[]>('/leave/types');
  return data;
}

export async function createLeaveType(
  payload: CreateLeaveTypePayload,
): Promise<LeaveType> {
  const { data } = await apiClient.post<LeaveType>('/leave/types', payload);
  return data;
}

export async function updateLeaveType(
  id: string,
  payload: Partial<CreateLeaveTypePayload>,
): Promise<LeaveType> {
  const { data } = await apiClient.patch<LeaveType>(
    `/leave/types/${id}`,
    payload,
  );
  return data;
}

export async function deleteLeaveType(id: string): Promise<void> {
  await apiClient.delete(`/leave/types/${id}`);
}

// ─── Leave Requests API ───────────────────────────────────────────────────────

export async function getLeaveRequests(
  params: GetLeaveRequestsParams,
): Promise<PaginatedLeaveResponse> {
  const { data } = await apiClient.get<PaginatedLeaveResponse>(
    '/leave/requests',
    { params },
  );
  return data;
}

export async function getLeaveRequestsPendingCount(): Promise<number> {
  const { data } = await apiClient.get<{ count: number }>(
    '/leave/requests/pending-count',
  );
  return data.count;
}

export async function createLeaveRequest(
  payload: CreateLeaveRequestPayload,
): Promise<LeaveRequest> {
  const { data } = await apiClient.post<LeaveRequest>(
    '/leave/requests',
    payload,
  );
  return data;
}

export interface UpdateLeaveRequestPayload {
  leaveTypeId?: string;
  dateFrom?: string;
  dateTo?: string;
  capacityType?: 'full_day' | 'half_day';
  totalDays?: number;
  note?: string;
}

export async function updateLeaveRequest(
  id: string,
  payload: UpdateLeaveRequestPayload,
): Promise<LeaveRequest> {
  const { data } = await apiClient.patch<LeaveRequest>(
    `/leave/requests/${id}`,
    payload,
  );
  return data;
}

export async function approveLeaveRequest(id: string): Promise<LeaveRequest> {
  const { data } = await apiClient.post<LeaveRequest>(
    `/leave/requests/${id}/approve`,
  );
  return data;
}

export async function rejectLeaveRequest(id: string): Promise<LeaveRequest> {
  const { data } = await apiClient.post<LeaveRequest>(
    `/leave/requests/${id}/reject`,
  );
  return data;
}

export async function recallLeaveRequest(id: string): Promise<LeaveRequest> {
  const { data } = await apiClient.post<LeaveRequest>(
    `/leave/requests/${id}/recall`,
  );
  return data;
}

export async function revokeLeaveRequest(id: string): Promise<LeaveRequest> {
  const { data } = await apiClient.post<LeaveRequest>(
    `/leave/requests/${id}/revoke`,
  );
  return data;
}

export async function deleteLeaveRequest(id: string): Promise<void> {
  await apiClient.delete(`/leave/requests/${id}`);
}

// ─── Calendar API ────────────────────────────────────────────────────────────

export async function getLeaveCalendar(
  params: GetLeaveCalendarParams,
): Promise<LeaveCalendarEntry[]> {
  const { data } = await apiClient.get<LeaveCalendarEntry[]>(
    '/leave/calendar',
    { params },
  );
  return data;
}

/** Export leave requests as a CSV blob. */
export async function exportLeaveRequests(
  params?: Omit<GetLeaveRequestsParams, 'page' | 'pageSize'>,
): Promise<Blob> {
  const { data } = await apiClient.get('/leave/requests/export', {
    params,
    responseType: 'blob',
  });
  return data as Blob;
}
